import { motion, AnimatePresence } from 'motion/react';
import { Eye, EyeOff, User, Mail, Phone, Calendar, Lock, CheckCircle2, Sparkles, Zap } from 'lucide-react';
import Field from '../components/Field';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export function IconInput({ icon: Icon, type = 'text', value, onChange, placeholder, hasError, ...rest }: {
  icon: React.ElementType; type?: string; value: string; onChange: (v: string) => void;
  placeholder?: string; hasError?: boolean;[k: string]: any;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="relative"
    >
      <motion.div
        animate={{ scale: hasError ? [1, 1.05, 1] : 1 }}
        transition={{ duration: 0.3 }}
        className="relative"
      >
        <Icon className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:w-4.5 sm:h-4.5 pointer-events-none z-10 transition-all duration-300"
          style={{ color: hasError ? '#ef4444' : 'var(--color-muted)' }} />
        <input
          type={type}
          className={`input input-with-icon pl-9 sm:pl-10 md:pl-12 pr-3 sm:pr-4 py-2 sm:py-2.5 md:py-3 text-sm sm:text-base [&:-webkit-autofill]:![background-color:transparent] [&:-webkit-autofill]:[transition:background-color_9999s] transition-all duration-300 focus:scale-[1.02] ${hasError ? 'border-red-400 shadow-red-100' : 'hover:border-[var(--color-accent)]'}`}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          {...rest}
        />
      </motion.div>
    </motion.div>
  );
}

export function PwInput({ value, onChange, placeholder, hasError }: {
  value: string; onChange: (v: string) => void; placeholder?: string; hasError?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="relative"
    >
      <motion.div
        animate={{ scale: hasError ? [1, 1.05, 1] : 1 }}
        transition={{ duration: 0.3 }}
        className="relative"
      >
        <Lock className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:w-4.5 sm:h-4.5 pointer-events-none z-10 transition-all duration-300"
          style={{ color: hasError ? '#ef4444' : 'var(--color-muted)' }} />
        <input
          type={show ? 'text' : 'password'}
          className={`input input-with-icon pl-9 sm:pl-10 md:pl-12 pr-9 sm:pr-10 md:pr-12 py-2 sm:py-2.5 md:py-3 text-sm sm:text-base [&:-webkit-autofill]:![background-color:transparent] [&:-webkit-autofill]:[transition:background-color_9999s] transition-all duration-300 focus:scale-[1.02] ${hasError ? 'border-red-400 shadow-red-100' : 'hover:border-[var(--color-accent)]'}`}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder ?? '•••••••'}
        />
        <motion.button
          type="button"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShow(s => !s)}
          className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 transition-all duration-300 hover:scale-110"
          style={{ color: hasError ? '#ef4444' : 'var(--color-muted)' }}
        >
          {show ? <EyeOff className="w-3.5 h-3.5 sm:w-4 sm:w-4.5 sm:h-4.5" /> : <Eye className="w-3.5 h-3.5 sm:w-4 sm:w-4.5 sm:h-4.5" />}
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

export function StrengthBar({ pw }: { pw: string }) {
  if (!pw) return null;
  const n = [pw.length >= 8, /[A-Z]/.test(pw), /[0-9]/.test(pw), /[^A-Za-z0-9]/.test(pw)].filter(Boolean).length;
  const colors = ['bg-red-500', 'bg-orange-400', 'bg-yellow-400', 'bg-emerald-500'];
  const labels = ['Weak', 'Fair', 'Good', 'Strong'];
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-2 space-y-1"
    >
      <div className="flex gap-1">
        {[0, 1, 2, 3].map(i => (
          <motion.div
            key={i}
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${i < n ? colors[n - 1] : 'bg-[var(--glass-border)]'}`}
          />
        ))}
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className={`text-[11px] font-medium ${n === 0 ? 'text-red-500' : n === 1 ? 'text-orange-400' : n === 2 ? 'text-yellow-400' : 'text-emerald-500'}`}
      >
        {labels[n - 1] ?? 'Very Weak'}
      </motion.p>
    </motion.div>
  );
}

// ── Shared register form body (exported for reuse in admin) ───────────────────
export function RegisterFormFields({
  form,
  errors,
  set,
  showRole = false,
}: {
  form: { name: string; email: string; phone: string; dob: string; password: string; confirm: string; role?: string };
  errors: Record<string, string>;
  set: (k: string) => (v: string) => void;
  showRole?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      className="space-y-4"
    >
      <Field label="Full Name" error={errors.name} required>
        <IconInput icon={User} value={form.name} onChange={set('name')} placeholder="Enter Your Name" hasError={!!errors.name} />
      </Field>

      <Field label="Email Address" error={errors.email} required>
        <IconInput icon={Mail} type="email" value={form.email} onChange={set('email')} placeholder="Enter Your Email" hasError={!!errors.email} />
      </Field>

      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <Field label="Phone Number" error={errors.phone} required>
          <IconInput icon={Phone} type="tel" value={form.phone} onChange={set('phone')} placeholder="Enter Your Phone Number" hasError={!!errors.phone} maxLength={10} />
        </Field>
        <Field label="Date of Birth" error={errors.dob} required>
          <IconInput icon={Calendar} type="date" value={form.dob} onChange={set('dob')} hasError={!!errors.dob}
            max={new Date().toISOString().split('T')[0]} />
        </Field>
      </motion.div>

      {showRole && (
        <Field label="Role">
          <select className="input" value={form.role ?? 'user'} onChange={e => set('role')(e.target.value)}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </Field>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <Field label="Password" error={errors.password} required>
          <PwInput value={form.password} onChange={set('password')} placeholder="Min. 8 characters" hasError={!!errors.password} />
          <StrengthBar pw={form.password} />
        </Field>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
      >
        <Field label="Confirm Password" error={errors.confirm} required>
          <PwInput value={form.confirm} onChange={set('confirm')} placeholder="Repeat your password" hasError={!!errors.confirm} />
          <AnimatePresence>
            {!errors.confirm && form.confirm && form.password === form.confirm && (
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1 text-emerald-600 pointer-events-none"
              >
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </Field>
      </motion.div>
    </motion.div>
  );
}

// ── Shared validation (exported for reuse) ────────────────────────────────────
export function validateRegisterForm(form: {
  name: string; email: string; phone: string; dob: string; password: string; confirm: string;
}) {
  const e: Record<string, string> = {};
  if (!form.name.trim()) e.name = 'Full name is required.';
  if (!form.email.trim()) e.email = 'Email is required.';
  else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email address.';
  if (!form.phone.trim()) e.phone = 'Phone number is required.';
  else if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) e.phone = 'Enter a valid 10-digit phone number.';
  if (!form.dob) e.dob = 'Date of birth is required.';
  if (!form.password) e.password = 'Password is required.';
  else if (form.password.length < 8) e.password = 'Password must be at least 8 characters.';
  if (!form.confirm) e.confirm = 'Please confirm your password.';
  else if (form.password !== form.confirm) e.confirm = 'Passwords do not match.';
  return e;
}

// ── Login page ────────────────────────────────────────────────────────────────
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Clear email error when user types
  const setEmailWithClear = (value: string) => {
    setEmail(value);
    if (errors.email) {
      setErrors(e => {
        const newErrors = { ...e };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  // Clear password error when user types
  const setPasswordWithClear = (value: string) => {
    setPassword(value);
    if (errors.password) {
      setErrors(e => {
        const newErrors = { ...e };
        delete newErrors.password;
        return newErrors;
      });
    }
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!email.trim()) e.email = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Enter a valid email.';
    if (!password) e.password = 'Password is required.';
    return e;
  };

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast('Welcome back! Login successful.', 'success');
    } else {
      setErrors({ form: res.error || 'Invalid email or password.' });
      toast(res.error || 'Login failed', 'error');
    }
  };

  return (
    <div className="min-h-[100vh] flex items-center justify-center px-4 py-8 bg-gradient-to-br from-[#f4fbf9] via-[#e8f6f3] to-[#d8f0ea] dark:from-[#0b120f] dark:via-[#101c18] dark:to-[#1a332a] relative overflow-hidden">
      {/* Premium animated background shapes */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.3 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -top-[100%] -left-[50%] w-[150vw] h-[150vw] rounded-full bg-gradient-to-r from-[#34a853]/20 to-[#4285f4]/20 blur-3xl z-0 pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.2 }}
        transition={{ duration: 2.5, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -top-[120%] -left-[70%] w-[160vw] h-[160vw] rounded-full bg-gradient-to-r from-[#fbbc04]/20 to-[#ea4335]/20 blur-3xl z-0 pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.15 }}
        transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -top-[140%] -left-[90%] w-[170vw] h-[170vw] rounded-full bg-gradient-to-r from-[#4285f4]/20 to-[#34a853]/20 blur-3xl z-0 pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.25 }}
        transition={{ duration: 2.2, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -bottom-[80%] -right-[30%] w-[120vw] h-[120vw] rounded-full bg-gradient-to-r from-[#ea4335]/20 to-[#fbbc04]/20 blur-3xl z-0 pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.2 }}
        transition={{ duration: 2.8, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -bottom-[100%] -right-[50%] w-[130vw] h-[130vw] rounded-full bg-gradient-to-r from-[#4285f4]/20 to-[#34a853]/20 blur-3xl z-0 pointer-events-none"
      />
      <div className="absolute bottom-[100%] -right-[50%] w-[130vw] h-[130vw] rounded-full bg-[#d8f0ea] dark:bg-[#152620] z-0 pointer-events-none"></div>

      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-[900px] sm:max-w-[950px] bg-[var(--color-bg)] dark:bg-[var(--color-bg-2)] shadow-[0_30px_80px_rgba(40,80,60,0.15)] rounded-3xl border border-[var(--glass-border)] flex flex-col md:flex-row overflow-auto min-h-[600px] md:min-h-[650px] max-h-[95vh] md:max-h-[90vh] relative z-10"
      >
        {/* Left Side - Form */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-center relative">
          <div className="text-center mb-6 sm:mb-8">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center mb-4 sm:mb-6"
            >
              <img src="/stone-bench-logo.png" alt="Stone Bench Logo" className="w-16 h-16 sm:w-20 sm:h-20 object-contain" />
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-2xl sm:text-3xl md:text-4xl font-black mb-2 text-[var(--color-text)] tracking-tight"
            >
              Login
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-[11px] sm:text-[12px] md:text-[13px] text-[var(--color-muted)]"
            >
              Welcome back to your financial journey
            </motion.p>
          </div>

          {errors.form && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 sm:p-4 rounded-xl text-[12px] sm:text-[13px] font-medium text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-center"
            >
              {errors.form}
            </motion.div>
          )}

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            onSubmit={handle}
            className="space-y-4 sm:space-y-5"
            noValidate
          >
            <Field label="Email Address" error={errors.email} required>
              <IconInput icon={Mail} type="email" value={email} onChange={setEmailWithClear}
                placeholder="you@example.com" hasError={!!errors.email} />
            </Field>
            <Field label="Password" error={errors.password} required>
              <PwInput value={password} onChange={setPasswordWithClear} hasError={!!errors.password} placeholder="•••••••" />
            </Field>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex justify-center pt-2"
            >
              <motion.a
                href="#"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="font-medium text-[11px] sm:text-[12px] text-[var(--color-text)] hover:text-[var(--color-accent)] transition-colors"
              >
                Forgot Your Password?
              </motion.a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex justify-center mt-4 sm:mt-6"
            >
              <motion.button
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.05 }}
                whileTap={{ scale: loading ? 1 : 0.95 }}
                className="btn btn-primary px-8 sm:px-10 md:px-12 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm disabled:opacity-60 shadow-xl shadow-[var(--color-accent)]/30 uppercase tracking-widest font-bold"
              >
                {loading ? 'Signing In…' : 'Sign In'}
              </motion.button>
            </motion.div>
          </motion.form>

          <div className="md:hidden mt-8 text-center border-t border-[var(--glass-border)] pt-6">
            <p className="text-[11px] text-[var(--color-muted)] mb-3">Don't have an account?</p>
            <Link to="/register" className="btn btn-ghost px-8 py-2 rounded-full text-[11px] uppercase tracking-widest font-bold">Sign Up</Link>
          </div>
        </div>

        {/* Right Side - Colored Panel */}
        <div className="hidden md:flex w-1/2 bg-gradient-to-br from-[var(--color-accent)] to-[#2a7f3e] text-white p-8 sm:p-10 md:p-12 flex-col items-center justify-center text-center relative"
          style={{ borderTopLeftRadius: '100px', borderBottomLeftRadius: '100px' }}>
          {/* Premium background effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/5 mix-blend-overlay"></div>
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute top-6 sm:top-10 right-6 sm:right-10 w-12 h-12 sm:w-20 sm:h-20 bg-white/10 rounded-full blur-xl"
          />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
            className="absolute bottom-6 sm:bottom-10 left-6 sm:left-10 w-10 h-10 sm:w-16 sm:h-16 bg-white/10 rounded-full blur-xl"
          />

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="relative z-10 w-full px-4 sm:px-6"
          >
            <motion.div
              initial={{ scale: 0, rotate: 180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mb-4 sm:mb-6"
            >
              <Sparkles className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-white/80" />
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-black mb-3 sm:mb-4 tracking-tight drop-shadow-sm text-white">Welcome Back!</h2>
            <p className="text-white/95 text-xs sm:text-sm mb-2 sm:mb-3 leading-relaxed">Welcome back to your financial journey.</p>
            <p className="text-white/80 text-xs sm:text-sm mb-6 sm:mb-8 leading-relaxed">
              Sign in to continue building, learning, and growing your wealth.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/register" className="inline-block border-2 border-white text-white px-8 sm:px-10 md:px-12 py-2.5 sm:py-3 rounded-full font-bold text-xs sm:text-sm hover:bg-white hover:text-[var(--color-accent)] transition-all duration-300 uppercase tracking-widest shadow-lg">
                Sign Up
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

// ── Register page ─────────────────────────────────────────────────────────────
export function Register() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', dob: '', password: '', confirm: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const set = (k: string) => (v: string) => {
    setForm(p => ({ ...p, [k]: v }));
    // Clear error for this field when user starts typing
    if (errors[k]) {
      setErrors(e => {
        const newErrors = { ...e };
        delete newErrors[k];
        return newErrors;
      });
    }
  };

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateRegisterForm(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setLoading(true);
    const res = await register(form.email, form.password, { name: form.name, phone: form.phone, dob: form.dob });
    setLoading(false);
    if (res.ok) {
      toast('Account created! Please login.', 'success');
      navigate('/login');
    } else {
      setErrors({ form: res.error || 'Registration failed' });
      toast(res.error || 'Registration failed', 'error');
    }
  };

  return (
    <div className="min-h-[100vh] flex items-center justify-center px-4 py-8 bg-gradient-to-br from-[#f4fbf9] via-[#e8f6f3] to-[#d8f0ea] dark:from-[#0b120f] dark:via-[#101c18] dark:to-[#1a332a] relative overflow-hidden">
      {/* Premium animated background shapes */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.3 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -top-[100%] -left-[50%] w-[150vw] h-[150vw] rounded-full bg-gradient-to-r from-[#34a853]/20 to-[#4285f4]/20 blur-3xl z-0 pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.2 }}
        transition={{ duration: 2.5, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -top-[120%] -left-[70%] w-[160vw] h-[160vw] rounded-full bg-gradient-to-r from-[#fbbc04]/20 to-[#ea4335]/20 blur-3xl z-0 pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.15 }}
        transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -top-[140%] -left-[90%] w-[170vw] h-[170vw] rounded-full bg-gradient-to-r from-[#4285f4]/20 to-[#34a853]/20 blur-3xl z-0 pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.25 }}
        transition={{ duration: 2.2, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -bottom-[80%] -right-[30%] w-[120vw] h-[120vw] rounded-full bg-gradient-to-r from-[#ea4335]/20 to-[#fbbc04]/20 blur-3xl z-0 pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.2 }}
        transition={{ duration: 2.8, repeat: Infinity, repeatType: "reverse" }}
        className="absolute -bottom-[100%] -right-[50%] w-[130vw] h-[130vw] rounded-full bg-gradient-to-r from-[#4285f4]/20 to-[#34a853]/20 blur-3xl z-0 pointer-events-none"
      />

      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-[900px] sm:max-w-[950px] md:max-w-[1000px] bg-[var(--color-bg)] dark:bg-[var(--color-bg-2)] shadow-[0_30px_80px_rgba(40,80,60,0.15)] rounded-3xl border border-[var(--glass-border)] flex flex-col md:flex-row max-h-[95vh] md:max-h-[90vh] relative z-10"
      >
        {/* Left Side - Colored Panel */}
        <div className="hidden md:flex w-[45%] bg-gradient-to-br from-[var(--color-accent)] to-[#2a7f3e] text-white p-8 sm:p-10 md:p-12 flex-col items-center justify-center text-center relative"
          style={{ borderTopRightRadius: '100px', borderBottomRightRadius: '100px' }}>
          {/* Premium background effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/5 mix-blend-overlay"></div>
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute top-6 sm:top-10 right-6 sm:right-10 w-12 h-12 sm:w-20 sm:h-20 bg-white/10 rounded-full blur-xl"
          />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
            className="absolute bottom-6 sm:bottom-10 left-6 sm:left-10 w-10 h-10 sm:w-16 sm:h-16 bg-white/10 rounded-full blur-xl"
          />

          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="relative z-10 w-full px-4 sm:px-6"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mb-4 sm:mb-6"
            >
              <Zap className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-white/80" />
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-black mb-3 sm:mb-4 tracking-tight drop-shadow-sm text-white">Hello, Friend!</h2>
            <p className="text-white/95 text-xs sm:text-sm mb-2 sm:mb-3 leading-relaxed">Welcome to your journey of smarter investing.</p>
            <p className="text-white/80 text-xs sm:text-sm mb-6 sm:mb-8 leading-relaxed">
              Create your account and unlock powerful tools to build your financial future.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/login" className="inline-block border-2 border-white text-white px-8 sm:px-10 md:px-12 py-2.5 sm:py-3 rounded-full font-bold text-xs sm:text-sm hover:bg-white hover:text-[var(--color-accent)] transition-all duration-300 uppercase tracking-widest shadow-lg">
                Sign In
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-[55%] p-6 sm:p-8 md:p-10 flex flex-col justify-center relative">
          <div className="text-center mb-4 sm:mb-6">
            <motion.div
              initial={{ scale: 0, rotate: 180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center mb-3 sm:mb-4"
            >
              <img src="/stone-bench-logo.png" alt="Stone Bench Logo" className="w-14 h-14 sm:w-16 sm:h-16 object-contain" />
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-2xl sm:text-3xl font-black mb-2 text-[var(--color-text)] tracking-tight"
            >
              Create Account
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-[11px] sm:text-[12px] text-[var(--color-muted)]"
            >
              Start your investment journey today
            </motion.p>
          </div>

          {errors.form && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-xl text-[12px] sm:text-[13px] font-medium text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-center"
            >
              {errors.form}
            </motion.div>
          )}

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            onSubmit={handle}
            className="space-y-3 sm:space-y-4 overflow-auto min-h-[400px]"
            noValidate
          >
            <RegisterFormFields form={form} errors={errors} set={set} />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="flex justify-center mt-3 sm:mt-4 pt-2"
            >
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.05 }}
                whileTap={{ scale: loading ? 1 : 0.95 }}
                className="btn btn-primary px-8 sm:px-10 md:px-12 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm disabled:opacity-60 shadow-xl shadow-[var(--color-accent)]/30 uppercase tracking-widest font-bold"
              >
                {loading ? 'Creating Account…' : 'Create Account'}
              </motion.button>
            </motion.div>
          </motion.form>

          <div className="md:hidden mt-6 text-center border-t border-[var(--glass-border)] pt-5">
            <p className="text-[11px] text-[var(--color-muted)] mb-3">Already have an account?</p>
            <Link to="/login" className="btn btn-ghost px-8 py-2 rounded-full text-[11px] uppercase tracking-widest font-bold">Sign In</Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
